import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { getUser } from '../lib/auth';
import { dmKey, getSeen, markSeen } from '../lib/seen';
import { formatListTime, messagePreview } from '../lib/format';

const TABS = [
  { key: 'chats', label: 'Chats' },
  { key: 'groups', label: 'Groups' },
];

// Left rail: identity header, search, Chats/Groups tabs, and the live list.
// Reads DMs from /api/chats and joined groups from /api/groups/user/:id.
// Unread is derived locally from the seen-store (same model as the app).
export default function Sidebar({ activeId, onSelect, onOpenSettings, className = '' }) {
  const [tab, setTab] = useState('chats');
  const [query, setQuery] = useState('');
  const [chats, setChats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const me = getUser();
  const myId = me?.userId;
  const initial = (me?.userName || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(false);
        // Load DMs and groups independently — a failure in one must not blank
        // the whole sidebar (allSettled, not all). We only show the error state
        // if BOTH fail.
        const [chatRes, groupRes] = await Promise.allSettled([
          api.get('/api/chats'),
          myId ? api.get(`/api/groups/user/${myId}`) : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        const chatData =
          chatRes.status === 'fulfilled' && Array.isArray(chatRes.value.data)
            ? chatRes.value.data
            : [];
        const groupData =
          groupRes.status === 'fulfilled' && Array.isArray(groupRes.value.data)
            ? groupRes.value.data
            : [];
        setChats(chatData);
        setGroups(groupData);
        if (chatRes.status === 'rejected')
          console.error('Failed to load chats:', chatRes.reason);
        if (groupRes.status === 'rejected')
          console.error('Failed to load groups:', groupRes.reason);
        if (chatRes.status === 'rejected' && groupRes.status === 'rejected') {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to load conversations:', err);
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [myId]);

  // Normalise DMs into list items (already sorted by lastMessageAt server-side).
  const chatItems = useMemo(
    () =>
      chats
        .filter((c) => c.otherUser)
        .map((c) => {
          const last = c.lastMessage;
          const lastMs = last?.createdAt ? new Date(last.createdAt).getTime() : 0;
          const incoming = last && String(last.senderId) !== String(myId);
          const unread = incoming && lastMs > getSeen(dmKey(c.otherUser._id));
          return {
            kind: 'dm',
            id: c._id,
            chatId: c._id,
            otherUserId: c.otherUser._id,
            title: c.otherUser.name || 'Unknown',
            photoURL: c.otherUser.photoURL || '',
            preview: messagePreview(last),
            time: c.lastMessageAt || last?.createdAt,
            unread,
          };
        }),
    [chats, myId]
  );

  // Joined groups (server sorts by createdAt; no group last-message preview yet).
  const groupItems = useMemo(
    () =>
      groups.map((g) => ({
        kind: 'group',
        id: g._id,
        groupId: g._id,
        title: g.name || 'Group',
        photoURL: g.icon || g.avatar || '',
        preview: `${(g.members && g.members.length) || 0} members`,
        time: g.createdAt,
        unread: false,
      })),
    [groups]
  );

  const items = tab === 'chats' ? chatItems : groupItems;
  const filtered = query
    ? items.filter((i) => i.title.toLowerCase().includes(query.toLowerCase()))
    : items;

  const handleSelect = (item) => {
    if (item.kind === 'dm') markSeen(dmKey(item.otherUserId));
    onSelect(item);
  };

  return (
    <aside
      className={`w-full h-full flex flex-col border-r border-slate-200 bg-sidebar ${className}`}
    >
      {/* Identity / settings header */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-slate-200">
        <div className="w-10 h-10 rounded-full bg-brand text-white grid place-items-center font-semibold">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink truncate">{me?.userName || 'NearIt'}</div>
          <div className="text-xs text-muted">Web</div>
        </div>
        <button
          onClick={onOpenSettings}
          title="Settings"
          className="w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-muted"
        >
          ⚙
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          className="w-full px-3 py-2 rounded-full bg-slate-100 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/40"
        />
      </div>

      {/* Tabs */}
      <div className="flex px-3 gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
              tab === t.key ? 'bg-brand/10 text-brand' : 'text-muted hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto mt-2">
        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-muted">Loading…</div>
        ) : error ? (
          <div className="px-6 py-12 text-center text-sm text-red-500">
            Couldn’t load conversations.
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted">
            {query
              ? 'No matches.'
              : tab === 'chats'
              ? 'Your chats will appear here.'
              : 'Your groups will appear here.'}
          </div>
        ) : (
          filtered.map((item) => (
            <ListItem
              key={item.id}
              item={item}
              active={item.id === activeId}
              onClick={() => handleSelect(item)}
            />
          ))
        )}
      </div>
    </aside>
  );
}

function ListItem({ item, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
        active ? 'bg-brand/10' : 'hover:bg-slate-50'
      }`}
    >
      {item.photoURL ? (
        <img
          src={item.photoURL}
          alt=""
          className="w-12 h-12 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-brand/20 text-brand grid place-items-center font-semibold shrink-0">
          {item.title.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-ink truncate">{item.title}</span>
          {item.time && (
            <span className={`text-xs shrink-0 ${item.unread ? 'text-brand' : 'text-muted'}`}>
              {formatListTime(item.time)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted truncate">{item.preview}</span>
          {item.unread && (
            <span className="shrink-0 w-2.5 h-2.5 rounded-full bg-brand" aria-label="unread" />
          )}
        </div>
      </div>
    </button>
  );
}
